import React, { useContext, useEffect } from 'react';
import { AuthContext } from '../context/AuthContext';
import { useAppSettings } from '../hooks/useAppSettings';
import useApi from './useApi';

/**
 * Hook for fetching user roles from the API.
 * 
 * Pattern follows OrcaWebsite's useGetRole:
 * - getUserRoles() is a simple function that triggers the API call
 * - A useEffect watches the state and processes the response
 * - This prevents infinite loops by not using useCallback with dependencies
 *   that change when roles are set
 */
const useGetUserRoles = () => {
    const { state, getApiData } = useApi();
    const { settings: appSettings } = useAppSettings();
    const { setRoles, setCanEdit, setCanEditChemGuard, setCanAudit, setIsLoadingRoles } = useContext(AuthContext);
    const [retryCount, setRetryCount] = React.useState(0);

    // Simple function to trigger the API call - no useCallback needed
    // This prevents infinite loops since the function reference doesn't change
    const getUserRoles = () => {
        if (!appSettings?.api) {
            console.warn('API configuration not available yet');
            return;
        }

        const fullUrl = `${appSettings.api}/GetRoles`;
        console.log('🌐 Calling user roles API:', fullUrl);

        setIsLoadingRoles(true);

        getApiData({
            endpoint: fullUrl,
        });
    };

    // Process API response in a separate useEffect (like OrcaWebsite pattern)
    useEffect(() => {
        if (state.status === 'success' && state.result !== undefined) {
            console.log('✅ GetRoles API response received:', state.result);
            console.log('📊 Response type:', typeof state.result, 'isArray:', Array.isArray(state.result));

            // Parse roles array and set permissions
            if (Array.isArray(state.result)) {
                const rolesArray = state.result as string[];
                console.log(`📋 Roles array length: ${rolesArray.length}`, rolesArray);

                // Check if user has any roles assigned
                if (rolesArray.length === 0) {
                    // Retry up to 2 times with 1 second delay (token might not be fully ready after redirect)
                    if (retryCount < 2) {
                        console.warn(`⚠️ Empty roles array received, retrying in 1 second (attempt ${retryCount + 1}/2)...`);
                        setTimeout(() => {
                            setRetryCount(retryCount + 1);
                            if (appSettings?.api) {
                                console.log('🔄 Retrying GetRoles API call...');
                                getApiData({ endpoint: `${appSettings.api}/GetRoles` });
                            }
                        }, 1000);
                        return; // Don't set access denied yet
                    }

                    console.error('❌ User has no roles assigned after retries - access denied');
                    console.error('❌ This means the API returned an empty array. Check:');
                    console.error('   1. AppsApi is running and has permissions for CommodityBook');
                    console.error('   2. User is member of required AD groups');
                    console.error('   3. Check CommodityBookPortalApi console logs for details');
                    setRoles([]);
                    setCanEdit(false);
                    setCanEditChemGuard(false);
                    setCanAudit(false);
                } else {
                    console.log(`✅ Successfully loaded ${rolesArray.length} roles:`, rolesArray);
                    setRoles(rolesArray);
                    setCanEdit(rolesArray.includes('CommodityBookEditor'));
                    setCanEditChemGuard(rolesArray.includes('CommodityBookChemGuardEditor'));
                    setCanAudit(rolesArray.includes('CommodityBookAuditor'));
                    setRetryCount(0); // Reset retry count on success
                }
            }

            setIsLoadingRoles(false);
        } else if (state.status === 'error') {
            console.error('GetRoles API error:', state.error);
            // Set fallback permissions on error
            setRoles([]);
            setCanEdit(false);
            setCanEditChemGuard(false);
            setCanAudit(false);
            setIsLoadingRoles(false);
        }
    }, [state, setRoles, setCanEdit, setCanEditChemGuard, setCanAudit, setIsLoadingRoles]);

    // Parse permissions from current state for immediate access
    const canEdit = state.result && Array.isArray(state.result)
        ? (state.result as string[]).includes('CommodityBookEditor')
        : false;
    const canEditChemGuard = state.result && Array.isArray(state.result)
        ? (state.result as string[]).includes('CommodityBookChemGuardEditor')
        : false;
    const canAudit = state.result && Array.isArray(state.result)
        ? (state.result as string[]).includes('CommodityBookAuditor')
        : false;

    return {
        state,
        canEdit,
        canEditChemGuard,
        canAudit,
        getUserRoles,
    };
};

export default useGetUserRoles;